
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useImmer } from "use-immer";
import { Dialog, Toast } from "antd-mobile";
import { useI18n } from "../../../hooks/useI18n";
import TopBarContainer from "./components/TopBarContainer";
import VoiceInput from "./components/VoiceInput";
import MessageItem from "./components/MessageItem";
import LoadingMessage from "./components/LoadingMessage";
import { v4 as uuidv4 } from "uuid";
import { useTTS } from "../../../hooks/useTTS";
import {
  getSessionMessages,
  getSessionDetailById,
  type MessageInfo,
} from "../../../lib/api/conversations";
import {
  getScenarioDetail,
  ScenarioDetail
} from "../../../lib/api/scenario";
import { useWebSocket } from "../../../hooks/useWebSocket";
import RankLoader from "@/components/Loaders/RankLoader";
import { sleep } from "@/utils/toolUtils";
import ConnectNetworkLoader from "@/components/Loaders/ConnectNetworkLoader";
import LottieAnimation from "../../../components/LottieAnimation/LottieAnimation";
import { useLottieAnimation } from "../../../hooks/useLottieAnimation";
import AlertDialog, { AlertDialogConfig } from "../../../components/AlertDialog/AlertDialog";

// 音频播放状态枚举
type AudioPlayState = "idle" | "loading" | "playing";

interface Message {
  id: string;
  type: "user" | "ai";
  content: string;
  speech_type: "text" | "video";
  duration?: number;
  isComplete?: boolean;
  audioPlayState?: AudioPlayState; // 播放状态
  showText?: boolean;
  title?: string;
  timestamp?: number;
  audioUrl?: string;
  isStreaming?: boolean;
  round_number?: number;
  speaker?: "user" | "ai";
  message_type?: "text" | "audio";
  // AI助答相关字段
  aiAssistance?: {
    hint_content: string;
    suggestions: string[];
    context: string;
  };
}

interface Session {
  id: string;
  created_at: string;
  updated_at: string;
}

// AI陪练、对话相关事件类型常量
const CHAT_EVENTS = {
  // 聊天消息
  CHAT_MESSAGE: "chat_message",
  // 聊天流式消息，使用chunk方式发送字符片段
  CHAT_STREAM: "chat_message_stream",
  // 聊天流式消息结束
  CHAT_STREAM_END: "chat_message_stream_end",
  // 更新消息数据
  UPDATE_MESSAGE_DATA: "update_message_data",
  // 更新会话状态
  UPDATE_SESSION_DATA: "update_session_data",
  // 聊天错误通知
  CHAT_WRONG_NOTICE: "chat_wrong_notice",
  // 聊天结束
  CHAT_COMPLETE: "chat_complete",
  // 聊天强制结束
  CHAT_FORCE_END: "chat_force_end",
  // 偏差分析
  DEVIATION_ANALYSIS: "deviation_analysis",
  // AI助答提示
  AI_ASSISTANCE_HINT: "ai_assistance_hint",
  // 错误消息
  ERROR: "error",
  // 超时结束
  CHAT_TIMEOUT_END: "chat_timeout_end",
  // 完成所有环节结束
  CHAT_ALLSTEPS_END: "chat_allsteps_end",
} as const;

// 事件类型定义
type ChatEventType = (typeof CHAT_EVENTS)[keyof typeof CHAT_EVENTS];

interface WebSocketMessage {
  type: ChatEventType;
  data: any;
  session_id?: string;
  message_id?: string;
}

const AiTrainingChat: React.FC = () => {
  // 路由相关
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const scenarioData = location.state?.scenarioData as
      | ScenarioDetail
      | undefined;
  const sessionToken = location.state?.sessionToken as string | undefined;
  
  // 从URL参数中获取scene_lang，避免刷新后丢失
  const sceneLangFromUrl = searchParams.get('scene_lang') || 'zh-CN';

  // 基础状态
  const [messages, setMessages] = useImmer<Message[]>([]);
  const [aiIconPosition, setAiIconPosition] = useState(40);
  const [showFinishBtn, setShowFinishBtn] = useState(true);
  const [showTimer, setShowTimer] = useState(false);
  const [timerText, setTimerText] = useState("00:00");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [isPlayingResponse, setIsPlayingResponse] = useState(false);
  const chatContentRef = useRef<HTMLDivElement>(null);

  // 偏差分析相关状态
  const [deviationScore, setDeviationScore] = useState(40);
  const [analysisReason, setAnalysisReason] = useState("");
  const [showAnalysisBubble, setShowAnalysisBubble] = useState(false);
  const [deviationCount, setDeviationCount] = useState(0); // 话题偏离次数计数器

  // 通知相关状态
  const [notificationData, setNotificationData] = useState<{
    notice_type: 'normal' | 'deviation' | 'poor_response' | 'excellent_response';
    notice_title: string;
    notice_content: string;
  } | undefined>(undefined);
  const [showNotification, setShowNotification] = useState(false);

  // AlertDialog相关状态
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alertDialogConfig, setAlertDialogConfig] = useState<AlertDialogConfig | null>(null);

  // 会话和WebSocket状态
  const [session, setSession] = useState<Session | null>(null);

  // 场景详情状态
  const [currentScenarioDetail, setCurrentScenarioDetail] = useState<ScenarioDetail | null>(null);

  // 计时器相关状态
  const [chatStartTime, setChatStartTime] = useState<number | null>(null);
  const [chatDuration, setChatDuration] = useState<number>(0); // 对话时长（秒）
  const chatTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { roleData } = (location.state as { roleData: any }) || {}; // 按你实际类型写

  // 判空处理：如果没有roleData或voice_id，使用默认值
  const voice_id =
      roleData?.role_data?.voice_id || "zh_male_M392_conversation_wvae_bigtts";

  // TTS和语音识别
  const ttsService = useTTS({
    voice: voice_id,
    onStart: (item) => {
      console.log("TTS开始播放:", item.text);
    },
    onEnd: (item) => {
      console.log("TTS播放结束:", item.text);
    },
    onError: (item, error) => {
      console.error("TTS播放错误:", error);
    },
  });

  // Lottie动画
  const {
    isPlaying: isLottieAnimationPlaying,
    currentConfig: lottieConfig,
    playFirework,
    handleAnimationComplete,
    handleAnimationClose,
  } = useLottieAnimation();

  // 停止所有音频播放的统一方法
  const stopAllAudioPlayback = useCallback(() => {
    console.log("停止所有音频播放");
    
    // 停止TTS服务
    ttsService.stopAll();
    
    // 如果有session，也停止session相关的音频
    if (session) {
      ttsService.stopSession(session.id);
    }
    
    // 更新所有消息的播放状态为idle
    setMessages((draft) => {
      draft.forEach((msg) => {
        msg.audioPlayState = "idle";
      });
    });
    
    // 停止所有可能的原生Audio对象（通过查找并暂停所有audio元素）
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach((audio) => {
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
  }, [session, ttsService]);

  // 滚动到底部的方法
  const scrollToBottom = useCallback(() => {
    if (chatContentRef.current) {
      // 使用 setTimeout 确保 DOM 更新完成后再滚动
      setTimeout(() => {
        if (chatContentRef.current) {
          chatContentRef.current.scrollTop =
              chatContentRef.current.scrollHeight;
        }
      }, 100);
    }
  }, []);

  // 格式化时间显示
  const formatDuration = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // 计算实际练习时间（基于会话创建时间）
  const calculateActualPracticeTime = useCallback(() => {
    if (!session?.created_at) return 0;

    const startTime = new Date(session.created_at);
    const currentTime = new Date();
    const diffInSeconds = Math.floor((currentTime.getTime() - startTime.getTime()) / 1000);

    return Math.max(0, diffInSeconds); // 确保不返回负数
  }, [session?.created_at]);

  // 获取场景详情的方法
  const fetchScenarioDetail = useCallback(async () => {
    if (!scenarioData?.id) return;

    try {
      const response = await getScenarioDetail(scenarioData.id);
      if (response.data) {
        setCurrentScenarioDetail(response.data);
      }
    } catch (error) {
      console.error('获取场景详情失败:', error);
    }
  }, [scenarioData?.id]);

  // 开始计时器
  const startChatTimer = useCallback(() => {
    const startTime = Date.now();
    setChatStartTime(startTime);

    // 设置定时器更新对话时长
    const timer = setInterval(() => {
      const duration = Math.floor((Date.now() - startTime) / 1000);
      setChatDuration(duration);
      setTimerText(formatDuration(duration));
    }, 1000);

    chatTimerRef.current = timer;
    return timer;
  }, [formatDuration]);

  // 处理流式消息块
  const handleChatMessageStream = useCallback((message: any) => {
    const streamData = message.data;
    const messageId = streamData.message_id;
    const chunk = streamData.chunk || "";

    if (chunk) {
      // 更新或创建流式消息
      setMessages((prevState) => {
        const draft = [...prevState];
        const existingMessageIndex = draft.findIndex(
            (msg: Message) => msg.id === messageId
        );

        if (existingMessageIndex !== -1) {
          // 更新现有消息，拼接chunk
          draft[existingMessageIndex] = {
            ...draft[existingMessageIndex],
            content: draft[existingMessageIndex].content + chunk,
            isStreaming: true,
            isComplete: false,
          };
        } else {
          // 移除其他流式消息，创建新的流式消息
          const streamingIndex = draft.findIndex(
              (msg: Message) => msg.isStreaming
          );
          if (streamingIndex !== -1) {
            draft.splice(streamingIndex, 1);
          }
          const newStreamingMessage: Message = {
            id: messageId,
            content: chunk,
            type: streamData.is_user ? "user" : "ai",
            speech_type: "text",
            isComplete: false,
            isStreaming: true,
            timestamp: Date.now(),
          };
          draft.push(newStreamingMessage);
        }
        return draft;
      });
      setShowLoading(false);
    }
  }, []);

  // 处理流式消息结束
  const handleChatMessageStreamEnd = useCallback(
      (message: any) => {
        const messageId = message.data.message_id;
        const finalContent = message.data.content;

        console.log("流式消息结束:", messageId, finalContent);

        setIsPlayingResponse(false);

        // 更新消息为最终完整内容并标记为完成
        setMessages((draft) => {
          const messageIndex = draft.findIndex(
              (msg: Message) => msg.id === messageId
          );
          if (messageIndex !== -1) {
            draft[messageIndex].content =
                finalContent || draft[messageIndex].content;
            draft[messageIndex].isStreaming = false;
            draft[messageIndex].isComplete = true;
          }
        });

        // AI回复完成后手动滚动到底部
        setTimeout(() => {
          scrollToBottom();
        }, 100);

        // 播放完整消息的TTS（使用统一的播放方法）
        if (finalContent && session) {
          setTimeout(() => {
            playAudioByMessageId(messageId, finalContent);
          }, 20);
        }
      },
      [session, ttsService, scrollToBottom]
  );

  // 处理错误消息
  const handleErrorMessage = useCallback((message: any) => {
    console.error("WebSocket错误消息:", message.data);
    setShowLoading(false);
    setIsPlayingResponse(false);
    setMessages((draft) => {
      // 移除所有流式消息
      const streamingIndex = draft.findIndex((msg: Message) => msg.isStreaming);
      if (streamingIndex !== -1) {
        draft.splice(streamingIndex, 1);
      }
    });
  }, []);

  // 处理偏差分析消息
  const handleDeviationAnalysis = useCallback((message: any) => {
    const data = message.data;
    console.log("收到偏差分析:", data);

    if (data.deviation_score !== undefined) {
      setDeviationScore(data.deviation_score);
      setAiIconPosition(data.deviation_score);
    }

    if (data.analysis_reason) {
      setAnalysisReason(data.analysis_reason);
    }

    // 处理话题偏离警告的递进式提醒
    if (data.notice_type === 'deviation') {
      setDeviationCount(prevCount => {
        const newCount = prevCount + 1;
        console.log(`话题偏离次数: ${newCount}`);

        let noticeTitle = "";
        let noticeContent = "";
        let noticeType: 'deviation' | 'deviation_warning' | 'deviation_severe' | 'deviation_final' = 'deviation';

        switch (newCount) {
          case 1:
            noticeTitle = t('ai.chat.topicDeviationReminderTitle', '话题偏离提醒');
            noticeContent = t('ai.chat.topicDeviationReminderContent', '检测到您的回答偏离了当前话题，请尽量围绕主题进行对话。');
            noticeType = 'deviation';
            break;
          case 2:
            noticeTitle = t('ai.chat.topicDeviationWarningTitle', '话题偏离警告');
            noticeContent = t('ai.chat.topicDeviationWarningContent', '这是第二次偏离话题，请注意保持对话的相关性，避免进一步偏离主题。');
            noticeType = 'deviation_warning';
            break;
          case 3:
            noticeTitle = t('ai.chat.topicDeviationSevereTitle', '严重偏离警告');
            noticeContent = t('ai.chat.topicDeviationSevereContent', '这是第三次偏离话题，请立即回到正确的对话轨道上，否则系统将强制结束对话。');
            noticeType = 'deviation_severe';
            break;
          default:
            // 第四次及以上，强制结束对话
            noticeTitle = t('ai.chat.topicDeviationForceEndTitle', '对话强制结束');
            noticeContent = t('ai.chat.topicDeviationForceEndContent', '由于多次偏离话题，系统将强制结束本次对话。感谢您的参与！');
            noticeType = 'deviation_final';

            // 强制结束对话逻辑
            setTimeout(() => {
              // 停止所有音频播放
              stopAllAudioPlayback();
              
              setShowLoading(false);
              setIsPlayingResponse(false);

              Dialog.alert({
                content: t('ai.chat.deviationForceEndDialogMessage', '由于多次偏离话题，对话已被系统强制结束'),
                confirmText: t('common.understood', '知道了'),
                onConfirm: () => {
                  navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
                },
              });
            }, 2000);
            break;
        }

        // 设置通知数据
        setNotificationData({
          notice_type: noticeType as any,
          notice_title: noticeTitle,
          notice_content: noticeContent
        });
        setShowNotification(true);

        return newCount;
      });
    } else if (data.notice_type && data.notice_title && data.notice_content) {
      // 其他类型的通知暂时不进行显示
      return;
      // 处理其他类型的通知数据
      setNotificationData({
        notice_type: data.notice_type as 'normal' | 'deviation' | 'poor_response' | 'excellent_response',
        notice_title: data.notice_title,
        notice_content: data.notice_content
      });
      setShowNotification(true);
    }
  }, [stopAllAudioPlayback, sessionId, navigate, t]);

  // 处理AI助答提示消息
  const handleAiAssistanceHint = useCallback((message: any) => {
    const data = message.data;
    const messageId = data.message_id;

    console.log("收到AI助答提示:", data);

    // 更新对应消息的AI助答数据
    setMessages((draft) => {
      const messageIndex = draft.findIndex(
          (msg: Message) => msg.id === messageId
      );
      if (messageIndex !== -1) {
        draft[messageIndex].aiAssistance = {
          hint_content: data.hint_content,
          suggestions: data.suggestions || [],
          context: data.context || "",
        };
      }
    });
  }, []);

  // 处理超时结束事件
  const handleChatTimeoutEnd = useCallback((message: any) => {
    const data = message.data;
    console.log("收到超时结束事件:", data);

    // 停止所有音频播放
    stopAllAudioPlayback();
    
    // 停止所有播放和加载状态
    setShowLoading(false);
    setIsPlayingResponse(false);

    // 显示超时结束提醒
    const config: AlertDialogConfig = {
      type: 'timeout',
      title: t('ai.chat.timeoutEndTitle', '对话超时结束'),
      message: data.message || t('ai.chat.timeoutEndMessage', '对话已超时结束，无法继续对话。'),
      confirmText: t('common.understood', '知道了'),
      showCancel: false,
      autoConfirmSeconds: 10,
      autoConfirmText: t('秒后自动确定', '{seconds}秒后自动确定'),
      onConfirm: () => {
        navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
      },
      onAutoConfirm: () => {
        navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
      },
      showLottie: true,
      lottieAnimation: 'snow.json'
    };

    setAlertDialogConfig(config);
    setShowAlertDialog(true);
  }, [stopAllAudioPlayback, sessionId, navigate, t]);

  // 处理完成所有环节结束事件
  const handleChatAllStepsEnd = useCallback((message: any) => {
    const data = message.data;
    console.log("收到完成所有环节结束事件:", data);

    // 停止所有音频播放
    stopAllAudioPlayback();
    
    // 停止所有播放和加载状态
    setShowLoading(false);
    setIsPlayingResponse(false);

    // 显示完成所有环节提醒
    const config: AlertDialogConfig = {
      type: 'complete',
      title: t('ai.chat.恭喜完成！', '恭喜完成！'),
      message: data.message || t('ai.chat.恭喜！您已完成所有陪练环节。', '恭喜！您已完成所有陪练环节。'),
      confirmText: t('ai.chat.viewAnalysis', '查看分析'),
      showCancel: false,
      autoConfirmSeconds: 8,
      autoConfirmText: t('ai.chat.{seconds}秒后自动跳转到分析页面', '{seconds}秒后自动跳转到分析页面'),
      onConfirm: () => {
        navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
      },
      onAutoConfirm: () => {
        navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
      },
      showLottie: true,
      lottieAnimation: 'caidai.json'
    };

    setAlertDialogConfig(config);
    setShowAlertDialog(true);
  }, [stopAllAudioPlayback, sessionId, navigate, t]);

  // WebSocket消息处理回调
  const handleWebSocketMessage = useCallback(
      (wsData: any) => {
        if (wsData.content.type === "ping" || wsData.content.type === "pong") {
          return;
        }
        if (wsData.content.type !== "chat_message_stream") {
          console.log("收到WebSocket消息====》:", wsData);
        }

        // 根据新的消息格式处理
        if (!(wsData.content && wsData.content.type)) return;

        const message = wsData.content;

        switch (message.type) {
          case CHAT_EVENTS.CHAT_MESSAGE:
            // 处理普通聊天消息
            console.log("收到聊天消息:", message.data);
            break;

          case CHAT_EVENTS.CHAT_STREAM:
            // 处理流式消息块
            if (message.data) {
              handleChatMessageStream(message);
            }
            break;

          case CHAT_EVENTS.CHAT_STREAM_END:
            // 处理流式消息结束
            if (message.data) {
              handleChatMessageStreamEnd(message);
            }
            break;

          case CHAT_EVENTS.UPDATE_MESSAGE_DATA:
            // 处理消息数据更新
            console.log("收到消息数据更新:", message.data);
            break;

          case CHAT_EVENTS.UPDATE_SESSION_DATA:
            // 处理会话状态更新
            console.log("收到会话状态更新:", message.data);
            break;

          case CHAT_EVENTS.CHAT_WRONG_NOTICE:
            // 处理聊天错误通知
            console.log("收到聊天错误通知:", message.data);
            break;

          case CHAT_EVENTS.CHAT_COMPLETE:
            // 处理聊天结束
            console.log("聊天已结束:", message.data);
            
            // 停止所有音频播放
            stopAllAudioPlayback();
            
            // 停止所有播放和加载状态
            setShowLoading(false);
            setIsPlayingResponse(false);
            
            Toast.show({
              content: t(
                  "ai.chat.practiceEndedThanks",
                  "对练已结束，感谢你的参与！"
              ),
              duration: 2000,
            });

            // 可选：导航回上一页或主页
            setTimeout(() => {
              navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
            }, 3000);
            // 播放烟花动画庆祝对话完成
            setTimeout(() => {
              playFirework({
                duration: 2000,
                onComplete: () => {
                  console.log("烟花动画播放完成");
                  // 显示感谢提示
                  // 可以添加一些UI反馈
                },
                onClose: () => {
                  console.log("烟花动画关闭");
                },
              });
            }, 500);
            break;

          case CHAT_EVENTS.CHAT_FORCE_END:
            // 处理聊天强制结束
            console.log("聊天被强制结束:", message.data);
            
            // 停止所有音频播放
            stopAllAudioPlayback();
            
            // 停止所有播放和加载状态
            setShowLoading(false);
            setIsPlayingResponse(false);
            
            // 显示强制结束的提示
            // Dialog.alert({
            //   content: t('ai.chat.forceEndMessage', '对话已被系统强制结束'),
            //   confirmText: t('common.understood', '知道了'),
            //   onConfirm: () => {
            //     // 可以添加跳转逻辑
            //     // navigate(-1);
            //   },
            // });
            break;

          case CHAT_EVENTS.DEVIATION_ANALYSIS:
            // 处理偏差分析
            handleDeviationAnalysis(message);
            break;

          case CHAT_EVENTS.AI_ASSISTANCE_HINT:
            // 处理AI助答提示
            handleAiAssistanceHint(message);
            break;

          case CHAT_EVENTS.ERROR:
            // 处理错误消息
            handleErrorMessage(message);
            break;

          case CHAT_EVENTS.CHAT_TIMEOUT_END:
            // 处理超时结束
            handleChatTimeoutEnd(message);
            stopAllAudioPlayback();
            break;

          case CHAT_EVENTS.CHAT_ALLSTEPS_END:
            // 处理完成所有环节结束
            handleChatAllStepsEnd(message);
            stopAllAudioPlayback();
            break;

          default:
            console.warn("未处理的消息类型:", message.type);
            break;
        }
      },
      [
        handleChatMessageStream,
        handleChatMessageStreamEnd,
        handleErrorMessage,
        handleDeviationAnalysis,
        handleAiAssistanceHint,
        handleChatTimeoutEnd,
        handleChatAllStepsEnd,
        stopAllAudioPlayback,
        session,
        sessionId,
        navigate,
        ttsService,
        t,
        playFirework,
      ]
  );

  // 使用useMemo缓存WebSocket配置对象，避免每次渲染都创建新对象
  const webSocketConfig = useMemo(
      () => ({
        url: "",
        autoConnect: !!sessionId,
        debug: true,
        handleOnlineStatus: false,
        onMessage: handleWebSocketMessage,
      }),
      [sessionId, sessionToken, handleWebSocketMessage]
  );

  // WebSocket连接
  const { isConnected, send: sendWsMessage } = useWebSocket(webSocketConfig);

  // 将MessageInfo转换为Message格式
  const convertMessageInfoToMessage = (messageInfo: MessageInfo): Message => {
    return {
      id: messageInfo.id,
      type: messageInfo.speaker === "user" ? "user" : "ai",
      content: messageInfo.content,
      speech_type: messageInfo.message_type === "audio" ? "video" : "text",
      duration: messageInfo.audio_duration,
      isComplete: true,
      audioPlayState: "idle",
      timestamp: new Date(messageInfo.timestamp).getTime(),
      audioUrl: messageInfo.audio_file_path,
      isStreaming: false,
      round_number: messageInfo.round_number,
      speaker: messageInfo.speaker,
      message_type: messageInfo.message_type,
      aiAssistance: messageInfo.ai_hint || {},
    };
  };

  // 初始化
  useEffect(() => {
    const initializeChat = async () => {
      setIsInitialLoading(true);

      try {
        // 获取场景详情
        await fetchScenarioDetail();

        // 启动计时器
        startChatTimer();

        // 获取真实的会话详情
        if (sessionId) {
          try {
            const sessionDetailResponse = await getSessionDetailById(sessionId);
            if (sessionDetailResponse.success && sessionDetailResponse.data) {
              const sessionData: Session = {
                id: sessionId,
                created_at: sessionDetailResponse.data.created_at,
                updated_at: sessionDetailResponse.data.updated_at || new Date().toISOString(),
              };
              setSession(sessionData);
            } else {
              // 如果获取失败，使用当前时间作为备用
              const sessionData: Session = {
                id: sessionId,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
              setSession(sessionData);
            }
          } catch (error) {
            console.error('获取会话详情失败:', error);
            // 如果获取失败，使用当前时间作为备用
            const sessionData: Session = {
              id: sessionId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setSession(sessionData);
          }

          // 获取历史消息
          try {
            const messagesResponse = await getSessionMessages(sessionId, {
              page: 1,
              pageSize: 100,
            });
            if (
                messagesResponse.success &&
                messagesResponse.data &&
                messagesResponse.data.list
            ) {
              const historyMessages = messagesResponse.data.list.map(
                  convertMessageInfoToMessage
              );
              setMessages((draft) => {
                draft.splice(0, draft.length, ...historyMessages);
              });

              // 如果没有历史消息，添加初始AI消息
              if (historyMessages.length === 0) {
                const initialMessage: Message = {
                  id: uuidv4(),
                  type: "ai",
                  content:
                      scenarioData?.opening_dialogue ||
                      t('ai.chat.welcomeMessage', '你好！欢迎来到AI陪练，我是你的AI助手。让我们开始今天的练习吧！'),
                  speech_type: "text",
                  isComplete: true,
                  timestamp: Date.now(),
                };
                setMessages((draft) => {
                  draft.push(initialMessage);
                });
              }
            } else {
              // API调用失败，添加初始AI消息
              const initialMessage: Message = {
                id: uuidv4(),
                type: "ai",
                content:
                    scenarioData?.opening_dialogue ||
                    t('ai.chat.welcomeMessage', '你好！欢迎来到AI陪练，我是你的AI助手。让我们开始今天的练习吧！'),
                speech_type: "text",
                isComplete: true,
                timestamp: Date.now(),
              };
              setMessages((draft) => {
                draft.push(initialMessage);
              });
            }
          } catch (error) {
            console.error("获取历史消息失败:", error);
            // 获取历史消息失败，添加初始AI消息
            const initialMessage: Message = {
              id: uuidv4(),
              type: "ai",
              content:
                  scenarioData?.opening_dialogue ||
                  t('ai.chat.welcomeMessage', '你好！欢迎来到AI陪练，我是你的AI助手。让我们开始今天的练习吧！'),
              speech_type: "text",
              isComplete: true,
              timestamp: Date.now(),
            };
            setMessages((draft) => {
              draft.push(initialMessage);
            });
          }
        }
      } catch (error) {
        console.error("初始化聊天失败:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    initializeChat();

    // 清理函数
    return () => {
      if (session) {
        ttsService.stopSession(session.id);
      }
      if (chatTimerRef.current) {
        clearInterval(chatTimerRef.current);
      }
    };
  }, [sessionId]);

  // 移除自动滚动监听，改为手动调用

  // 初始化完成后滚动到底部并播放开场白
  useEffect(() => {
    if (!isInitialLoading) {
      scrollToBottom();

      // 自动播放开场白（第一条AI消息）
      if (messages.length === 1 && session) {
        const firstAiMessage = messages.find(
            (msg) => msg.type === "ai" && msg.speech_type === "text"
        );
        if (firstAiMessage) {
          setTimeout(() => {
            playAudioByMessageId(
                firstAiMessage.id,
                firstAiMessage.content,
                firstAiMessage.audioUrl
            );
          }, 100); // 延迟播放，确保界面完全加载
        }
      }
      if (messages.length > 2 && session) {
        const lastAiMessage = [...messages]
            .reverse()
            .find((msg) => msg.type === "ai" && msg.speech_type === "text");
        if (lastAiMessage) {
          setTimeout(() => {
            playAudioByMessageId(
                lastAiMessage.id,
                lastAiMessage.content,
                lastAiMessage.audioUrl
            );
          }, 1000); // 延迟播放，确保界面完全加载
        }
      }
    }
  }, [isInitialLoading, session]);

  // 向聊天列表添加消息的方法
  const pushMessageToChatList = useCallback((message: Message) => {
    setMessages((draft) => {
      draft.push(message);
    });
  }, []);

  const handleSendMessage = useCallback(
      async (
          content: string,
          type: "text" | "voice" = "text",
          audioBlob?: Blob,
          fileUrl?: string,
          duration?: number
      ) => {
        console.log(content.trim(), sessionId, isConnected);

        if (!content.trim() || !sessionId || !isConnected) {
          console.warn("无法发送消息: 会话未创建或WebSocket未连接");
          return;
        }

        try {
          // 生成消息ID
          const messageId = uuidv4();
          // 添加用户消息到聊天列表
          const userSendMessage: Message = {
            id: messageId,
            type: "user",
            content,
            audioUrl: fileUrl,
            speech_type: type === "voice" ? "video" : "text",
            duration: type === "voice" ? duration : undefined,
            isComplete: true,
            timestamp: Date.now(),
          };

          // 添加用户消息后手动滚动到底部
          setTimeout(async () => {
            pushMessageToChatList(userSendMessage);
            await sleep(100);
            scrollToBottom();
            await sleep(100);
            setShowLoading(true);
            setIsPlayingResponse(true);
            await sleep(100);
            scrollToBottom();
          }, 150);

          // 通过WebSocket发送消息（不在这里添加用户消息到界面，等待WebSocket返回）
          const messageData = {
            type: "chat_message",
            data: {
              content,
              streaming: true,
              message_type: type === "voice" ? "audio" : "text",
              session_id: sessionId,
              audioUrl: fileUrl, // 如果有上传的音频文件URL
              has_audio: !!audioBlob, // 标识是否包含音频数据
            },
            session_id: sessionId,
            message_id: messageId,
          };

          console.log("发送WebSocket消息:", messageData);
          sendWsMessage(messageData);

          // 更新AI图标位置（模拟评分变化）
          const newPosition = Math.max(
              10,
              Math.min(90, aiIconPosition + (Math.random() - 0.5) * 20)
          );
          setAiIconPosition(newPosition);
        } catch (error) {
          console.error("发送消息失败:", error);
          setShowLoading(false);
          setIsPlayingResponse(false);

          // 显示错误消息
          const errorMessage: Message = {
            id: uuidv4(),
            type: "ai",
            content: "抱歉，发送消息时出现错误，请稍后重试。",
            speech_type: "text",
            isComplete: true,
            timestamp: Date.now(),
          };
          setMessages((draft) => {
            draft.push(errorMessage);
          });
        }
      },
      [sessionId, sendWsMessage, isConnected, aiIconPosition]
  );

  // 格式化内容用于TTS播放：移除中英文括号及其内容
  const formatContentForTTS = useCallback((content: string): string => {
    if (!content) return "";
    // 移除中文括号（）和英文括号()及其内部的内容
    return content.replace(/[（(][^）)]*[）)]/g, '').trim();
  }, []);

  // 统一的音频播放方法
  const playAudioByMessageId = useCallback(
      (messageId: string, content: string, audioUrl?: string) => {
        if (!session) {
          console.warn("无法播放音频: 会话未创建");
          return;
        }
        
        // 格式化内容，移除括号内的文字（用于TTS播放）
        const formattedContent = formatContentForTTS(content);
        
        console.log("原始内容:", content);
        console.log("格式化后的TTS内容:", formattedContent);
        
        // 如果格式化后内容为空，不播放
        if (!formattedContent) {
          console.warn("格式化后内容为空，跳过TTS播放");
          return;
        }

        // 更新消息状态的通用方法
        const updateMessageState = (
            id: string,
            audioPlayState: AudioPlayState
        ) => {
          setMessages((draft) => {
            const msg = draft.find((m) => m.id === id);
            if (msg) {
              msg.audioPlayState = audioPlayState;
            } else {
              console.log("DEBUG: 未找到对应消息, id:", id);
            }
          });
        };

        // 停止所有音频播放
        const stopAllAudio = () => {
          ttsService.stopAll();
          setMessages((draft) => {
            draft.forEach((msg) => {
              msg.audioPlayState = "idle";
            });
          });
        };

        // 停止其他消息的音频播放（不包括当前消息）
        const stopOtherAudio = (currentMessageId: string) => {
          ttsService.stopAll();
          setMessages((draft) => {
            draft.forEach((msg) => {
              if (msg.id !== currentMessageId) {
                msg.audioPlayState = "idle";
              }
            });
          });
        };

        // 获取当前消息状态
        const currentMessage = messages.find((m) => m.id === messageId);
        const currentState = currentMessage?.audioPlayState || "idle";

        // 如果当前消息正在播放，则停止播放
        if (currentState === "playing") {
          console.log(`停止播放消息: ${content}`);
          stopAllAudio();
          return;
        }

        // 如果正在加载中，不允许重复点击
        if (currentState === "loading") {
          console.log("音频正在加载中，请稍候...");
          return;
        }

        // 停止其他正在播放的音频（不包括当前消息）
        stopOtherAudio(messageId);

        // 设置当前消息为加载状态
        updateMessageState(messageId, "loading");

        // 如果消息已有音频URL，直接播放
        if (audioUrl) {
          const audio = new Audio(audioUrl);

          audio.onloadeddata = () => {
            // 音频加载完成，开始播放
            updateMessageState(messageId, "playing");
          };

          audio.onended = () => {
            updateMessageState(messageId, "idle");
          };

          audio.onerror = () => {
            console.error("音频播放失败");
            updateMessageState(messageId, "idle");
          };

          audio.play().catch((error) => {
            console.error("音频播放失败:", error);
            updateMessageState(messageId, "idle");
          });
        } else {
          // 使用ttsService hook进行播放（使用格式化后的内容）
          ttsService
              .playOnce(formattedContent, {
                onSynthesizing: (item) => {
                  updateMessageState(messageId, "loading");
                },
                onSynthesizeComplete: (item) => {
                  updateMessageState(messageId, "playing");
                },
                onStart: (item) => {
                  updateMessageState(messageId, "playing");
                },
                onEnd: (item) => {
                  updateMessageState(messageId, "idle");
                },
                onError: (item, errorMsg) => {
                  updateMessageState(messageId, "idle");
                },
              })
              .catch((error) => {
                console.log("DEBUG: 设置状态为idle (异常), messageId:", messageId);
                updateMessageState(messageId, "idle");
              });
        }
      },
      [session, ttsService, messages, formatContentForTTS]
  );

  const handlePlayAudio = useCallback(
      (message: Message, index: number) => {
        playAudioByMessageId(message.id, message.content, message.audioUrl);
      },
      [playAudioByMessageId]
  );

  const handleViewTips = (message: Message, index: number) => {
    Dialog.alert({
      content: t(
          "ai.chat.thinkingTip",
          "提示：你可以尝试从不同的角度来思考这个问题，比如考虑实际应用场景或者相关的理论知识。"
      ),
      confirmText: t("common.understood", "知道了"),
    });
  };
  const startRecording = () => {
    console.log("startRecording");

    ttsService.stopAll();
  };

  // 通知关闭回调
  const handleNotificationClose = useCallback(() => {
    setShowNotification(false);
    // setNotificationData(undefined);
  }, []);

  const handleFinishClick = useCallback(() => {
    console.log("handleFinishClick");

    if (!sessionId || !isConnected) {
      console.warn(
          t(
              "ai.chat.cannotEndConversation",
              "无法结束对话: 会话未创建或WebSocket未连接"
          )
      );
      return;
    }

    // 获取实际练习时间和最短练习时长
    const actualPracticeTime = calculateActualPracticeTime();
    const minDuration = currentScenarioDetail?.min_practice_duration || 0;
    const maxDuration = currentScenarioDetail?.max_practice_duration || 0;



    if (actualPracticeTime < minDuration) {
      const remainingTime = minDuration - actualPracticeTime;
      const remainingMinutes = Math.ceil(remainingTime / 60);
      const minMinutes = Math.ceil(minDuration / 60);
      const formattedActualTime = formatDuration(actualPracticeTime);

      const config: AlertDialogConfig = {
        type: 'warning',
        title: t('ai.chat.insufficientPracticeTimeTitle', '练习时间不足'),
        message: t('ai.chat.insufficientPracticeTimePrefix', '为了保证练习效果，建议至少练习') +
                minMinutes +
                t('ai.chat.insufficientPracticeTimeMiddle', '分钟。') ,
        confirmText: t('ai.chat.continuePractice', '继续练习'),
        cancelText: '',
        showCancel: false,
        onConfirm: () => {
          // 继续练习，不做任何操作
        },
        showLottie: false
      };

      setAlertDialogConfig(config);
      setShowAlertDialog(true);
      return;
    }

    // 时长足够，直接显示确认对话框
    proceedToFinish();
  }, [sessionId, isConnected, calculateActualPracticeTime, currentScenarioDetail?.min_practice_duration, formatDuration, t]);

  // 执行结束对话的逻辑
  const proceedToFinish = useCallback(() => {
    const config: AlertDialogConfig = {
      type: 'confirm',
      title: t("ai.chat.confirmEndPractice", "确定要结束本次对练吗？"),
      message: t('ai.chat.endRedirectTip', '结束后将跳转到分析页面查看本次对练的详细分析。'),
      confirmText: t("common.confirm", "确定结束"),
      cancelText: t("common.cancel", "继续对练"),
      showCancel: true,
      onConfirm: () => {
        try {
          // 清理计时器
          if (chatTimerRef.current) {
            clearInterval(chatTimerRef.current);
            chatTimerRef.current = null;
          }

          // 发送聊天结束事件到后端
          const finishData = {
            type: CHAT_EVENTS.CHAT_COMPLETE,
            data: {
              session_id: sessionId,
              end_time: new Date().toISOString(),
              reason: "user_finish", // 用户主动结束
            },
            session_id: sessionId,
            message_id: uuidv4(),
          };

          console.log("发送聊天结束事件:", finishData);
          sendWsMessage(finishData);

          // 停止所有音频播放
          stopAllAudioPlayback();
        } catch (error) {
          console.error("结束对话失败:", error);
        }
      },
      onCancel: () => {
        // 继续对话，不做任何操作
      },
      showLottie: false,
      lottieAnimation: 'snow.json'
    };

    setAlertDialogConfig(config);
    setShowAlertDialog(true);
  }, [sessionId, sendWsMessage, stopAllAudioPlayback, t]);

  // 强制结束对话的方法（可用于异常情况或系统触发）
  const handleForceEndChat = useCallback(
      (reason?: string) => {
        if (!sessionId || !isConnected) {
          console.warn("无法强制结束对话: 会话未创建或WebSocket未连接");
          return;
        }

        try {
          // 发送聊天强制结束事件到后端
          const forceEndData = {
            type: CHAT_EVENTS.CHAT_FORCE_END,
            data: {
              session_id: sessionId,
              end_time: new Date().toISOString(),
              reason: reason || "force_end", // 强制结束原因
            },
            session_id: sessionId,
            message_id: uuidv4(),
          };

          console.log("发送聊天强制结束事件:", forceEndData);
          sendWsMessage(forceEndData);

          // 停止所有音频播放
          stopAllAudioPlayback();

          // 停止所有播放和加载状态
          setShowLoading(false);
          setIsPlayingResponse(false);
        } catch (error) {
          console.error("强制结束对话失败:", error);
        }
      },
      [sessionId, isConnected, sendWsMessage, stopAllAudioPlayback]
  );

  // 处理时间结束事件（前端计时器倒计时结束）
  const handleTimeUp = useCallback(() => {
    console.log("练习时间已到，自动结束");
    
    // 如果已经在走结束流程，可以避免重复调用，但这里简单起见直接调用强制结束
    // 这里使用 force_end 或者 user_finish 取决于业务定义，既然是超时，可能算 force_end 或者 timeout
    // 假设后端能识别 reason 为 "timeout"
    handleForceEndChat("max_duration_reached");

    // 显示提示弹窗
    const config: AlertDialogConfig = {
      type: 'timeout',
      title: t('ai.chat.timeoutEndTitle', '练习时长已达上限'),
      message: t('ai.chat.maxDurationReachedMessage', '本次对练已完成，正在生成分析报告。'),
      confirmText: t('common.understood', '知道了'),
      showCancel: false,
      autoConfirmSeconds: 5,
      autoConfirmText: t('ai.chat.{seconds}秒后自动跳转到分析页面', '{seconds}秒后自动跳转到分析页面'),
      onConfirm: () => {
        navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
      },
      onAutoConfirm: () => {
        navigate(`/ai-training/analysis/${sessionId}`, { replace: true });
      },
      showLottie: true,
      lottieAnimation: 'snow.json'
    };

    setAlertDialogConfig(config);
    setShowAlertDialog(true);

  }, [handleForceEndChat, t, navigate, sessionId]);

  return (
      <motion.div
          className='min-h-screen bg-gray-50 flex flex-col overflow-y-auto [&::-webkit-scrollbar]:hidden'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
      >
        {/* 网络连接蒙层 */}
        {!isConnected && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='fixed  inset-0 bg-black/20 backdrop-blur-sm z-50 flex flex-col items-center justify-center'
            >
              <ConnectNetworkLoader />
              <div className='mt-4 text-gray-600 text-base font-medium'>
                {t("ai.chat.connectingToServer", "正在连接服务器...")}
              </div>
              <div className='mt-2 text-gray-400 text-sm'>
                {t(
                    "ai.chat.establishingSecureConnection",
                    "请稍候，正在建立安全连接"
                )}
              </div>
            </motion.div>
        )}
        {/* 顶部状态栏 - 固定在顶部 */}
        <div className='md:max-w-2xl lg:max-w-3xl xl:max-w-3xl mx-auto w-full'>
          <TopBarContainer
              // TopStatusBar props
              aiIconPosition={deviationScore}
              showFinishBtn={showFinishBtn}
              showTimer={showTimer}
              timerText={timerText}
              onFinishClick={handleFinishClick}
              analysisReason={analysisReason}
              showAnalysisBubble={showAnalysisBubble}
              onToggleAnalysisBubble={() =>
                  setShowAnalysisBubble(!showAnalysisBubble)
              }
              // 时长相关参数
              chatDuration={chatDuration}
              minDuration={currentScenarioDetail?.min_practice_duration || 0}
              maxDuration={currentScenarioDetail?.max_practice_duration || 0}
              scenarioDetail={currentScenarioDetail}
              // NotificationBar props
              notification={notificationData}
              showNotification={showNotification}
              onNotificationClose={handleNotificationClose}
              notificationDuration={5000}
              onTimeUp={handleTimeUp}
          />
        </div>

        {/* 聊天内容区域 - 可滚动 */}
        <div
            ref={chatContentRef}
            className='flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden'
            style={{ paddingBottom: "120px", maxHeight: "calc(100vh - 160px)" }}
        >
          {/* 响应式容器 */}
          <div className='px-4 md:max-w-2xl m-auto lg:max-w-3xl xl:max-w-3xl'>
            {/* 初始加载状态 */}
            {isInitialLoading && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    className='mt-24'
                >
                  <RankLoader />
                </motion.div>
            )}

            {/* 消息列表 */}
            {messages.map((message, index) => {
              const isLast = index === messages.length - 1;
              return (
                  <motion.div
                      key={message.id}
                      className='mb-3'
                      initial={isLast ? { opacity: 0, y: 30 } : { opacity: 1, y: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={isLast ? { duration: 0.16 } : { duration: 0 }}
                  >
                    <MessageItem
                        message={message}
                        index={index}
                        isLast={isLast}
                        onPlayAudio={handlePlayAudio}
                        onViewTips={handleViewTips}
                        showPlayButton={true}
                        showAiAssistButton={
                            message.type === "ai" && isLast && messages.length > 1
                        }
                    />
                  </motion.div>
              );
            })}

            {/* 加载状态 */}
            <AnimatePresence>
              {showLoading && (
                  <motion.div
                      className='flex justify-start m-4'
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                  >
                    <LoadingMessage />
                  </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* AI回复提示 */}
        {isPlayingResponse && (
            <div className='bg-orange-50 py-2'>
              <div className='max-w-sm mx-auto md:max-w-2xl lg:max-w-4xl xl:max-w-6xl '>
                <div className='flex items-center'>
                  <svg
                      className='w-5 h-5 text-orange-500 mr-2'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' />
                  </svg>
                  <span className='text-xs font-normal text-orange-500'>
                {t(
                    "ai.chat.waitForAiResponse",
                    "请耐心等待AI回复完成后，再发送新消息哦"
                )}
              </span>
                </div>
              </div>
            </div>
        )}

      {/* 底部输入区域 */}
      <div className='fixed bottom-0 left-0 right-0 bg-white'>
        <div className='md:max-w-2xl m-auto lg:max-w-3xl xl:max-w-3xl'>
          <VoiceInput
            onSendMessage={handleSendMessage}
            isStartRecording={startRecording}
            disabled={isPlayingResponse || showLoading}
            sceneLang={sceneLangFromUrl} // 使用从URL参数读取的场景语言，避免刷新后丢失
            sessionId={sessionId} // 传递会话ID，用于记录ASR使用情况
          />
        </div>
      </div>

        {/* 烟花动画 */}
        {isLottieAnimationPlaying && lottieConfig && (
            <LottieAnimation
                animationData={lottieConfig.animationData}
                loop={lottieConfig.loop}
                autoplay={lottieConfig.autoplay}
                speed={lottieConfig.speed}
                fullscreen={lottieConfig.fullscreen}
                showCloseButton={lottieConfig.showCloseButton}
                onComplete={handleAnimationComplete}
                onClose={handleAnimationClose}
                zIndex={10000}
            />
        )}

        {/* AlertDialog弹窗 */}
        {alertDialogConfig && (
            <AlertDialog
                visible={showAlertDialog}
                config={alertDialogConfig}
                onClose={() => {
                  setShowAlertDialog(false);
                  setAlertDialogConfig(null);
                }}
            />
        )}
      </motion.div>
  );
};

export default AiTrainingChat;